import { from, Observable } from 'rxjs';
import Swal, { SweetAlertIcon, SweetAlertOptions } from 'sweetalert2';

export enum DialogType {
  warning = 'warning',
  error = 'error',
  success = 'success',
  info = 'info',
  question = 'question',
}

export interface DialogOptions {
  message: string;
  type: DialogType;
  isHTML?: boolean;
  acceptText?: string;
  cancelText?: string;
}

export abstract class Dialog {
  /**
   * Método público para mostrar un diálogo.
   * @param message - Mensaje que se mostrará.
   * @param type - Tipo de diálogo (warning, error, etc.).
   * @param isHTML - Si es true, el mensaje se interpreta como HTML.
   * @param acceptText - Texto para el botón de aceptar.
   * @param cancelText - Texto para el botón de cancelar.
   */
  public static showDialog(
    message: string,
    type: DialogType,
    isHTML: boolean = false,
    acceptText: string = 'Aceptar',
    cancelText: string = 'Cancelar'
  ): Observable<boolean> {
    return from(this.openDialog({
      message,
      type,
      isHTML,
      acceptText,
      cancelText
    }));
  }

  /**
   * Método privado para configurar y mostrar un diálogo de SweetAlert2.
   * @param options - Configuración del diálogo.
   */
  private static openDialog(options: DialogOptions): Promise<boolean> {
    const defaultConfig = {
      title: this.getTitleByType(options.type),
      icon: options.type as SweetAlertIcon,
      confirmButtonText: options.acceptText || 'Aceptar',
      backdrop: false,
      allowOutsideClick: false,
      customClass: {
        confirmButton: 'button-primary',
        cancelButton: 'button-secondary'
      },
    };

    const config: SweetAlertOptions = {
      ...defaultConfig,
      ...this.getDialogSpecificOptions(options)
    } as SweetAlertOptions;

    return Swal.fire(config).then((result) => result.isConfirmed || false);
  }

  /**
   * Obtiene el título del diálogo basado en su tipo.
   * @param type - Tipo de diálogo.
  */
  private static getTitleByType(type: DialogType): string {
    const currentLanguage = localStorage.getItem('appLanguage') || 'es';

    const titles: Record<DialogType, string> = {
      [DialogType.success]: currentLanguage === 'es' ? '¡Éxito!' : '¡Success!',
      [DialogType.error]: currentLanguage === 'es' ? '¡Error!' : '¡Error!',
      [DialogType.warning]: currentLanguage === 'es' ? '' : '',
      [DialogType.info]: currentLanguage === 'es' ? '' : '',
      [DialogType.question]: currentLanguage === 'es' ? 'Confirmación requerida' : 'Confirmation required',
    };
    return titles[type] || '';
  }

  /**
   * Obtiene configuraciones adicionales específicas del tipo de diálogo.
   * @param options - Opciones del diálogo.
  */
  private static getDialogSpecificOptions(options: DialogOptions): Partial<SweetAlertOptions> {
    if (options.type === DialogType.question) {
      return {
        text: options.message,
        showCancelButton: true,
        cancelButtonText: options.cancelText || 'Cancelar',
        confirmButtonText: options.acceptText || 'Aceptar',
      } as Partial<SweetAlertOptions>;
    }

    return options.isHTML
      ? { html: options.message } as Partial<SweetAlertOptions>
      : { text: options.message } as Partial<SweetAlertOptions>;
  }
}
